import type { ComponentPropsWithoutRef } from 'react';
import { useMemo, useCallback, useState } from 'react';
import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';

import { useTheme, Button, Box, Text, Heading } from '@simplywallst/ui-core';

import ChevronDownSmallSolid from '@simplywallst/ui-core/icons/ChevronDownSmallSolid';

import { useWatchlists } from '@/hooks/useWatchlist';

import { Menu, Item, Section } from '@/components/Menu';

import { Option } from './Option';
import { CreateNewWatchlistOption } from './CreateNewWatchlistOption';

import { WATCHLISTS_LIMIT } from '../../constants';

const CreateWatchlistModal = loadable(() => import('../CreateWatchlistModal'));
const WatchlistsLimitModal = loadable(() => import('../WatchlistsLimitModal'));

type Key = Parameters<
  NonNullable<ComponentPropsWithoutRef<typeof Menu>['onAction']>
>[0];

interface Props {
  watchlistId: string | null;
  onSelect: (value: string | null) => void;
}

const CREATE_NEW_WATCHLIST_KEY = 'create-new-watchlist';

export const WatchlistDropdown = ({ watchlistId, onSelect }: Props) => {
  const theme = useTheme();
  const { data: watchlists } = useWatchlists();
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'watchlist-dropDown',
  });

  const [modal, setModal] = useState<'create' | 'limited' | null>(null);

  const handleCloseModal = useCallback(() => {
    setModal(null);
  }, []);

  const handleAction = useCallback(
    (value: Key) => {
      switch (value) {
        case CREATE_NEW_WATCHLIST_KEY:
          trackEvent({
            action: 'click',
            modifier: 'createNewWatchlist',
          });
          if (watchlists && watchlists.length >= WATCHLISTS_LIMIT) {
            setModal('limited');
          } else {
            setModal('create');
          }
          break;
        default:
          onSelect(String(value));
          trackEvent({
            action: 'click',
            modifier: 'changeWatchlist',
            watchlistId: String(value),
          });
          break;
      }
    },
    [onSelect, trackEvent, watchlists]
  );

  const watchlistsItems = useMemo(
    () =>
      watchlists?.map((watchlist) => {
        return {
          label: watchlist.name,
          value: watchlist.id,
          companyCount: watchlist.items.length,
        };
      }) || [],
    [watchlists]
  );

  const items = useMemo(
    () => [
      {
        label: 'Watchlists',
        value: 'watchlists',
        children: watchlistsItems,
      },
      {
        label: 'Other options',
        value: 'other-options',
        children: [
          {
            label: 'Create new watchlist',
            value: CREATE_NEW_WATCHLIST_KEY,
          },
        ],
      },
    ],
    [watchlistsItems]
  );

  const watchlistName = useMemo(
    () =>
      watchlists?.filter((watchlists) => watchlists.id === watchlistId)[0]
        ?.name || '',
    [watchlists, watchlistId]
  );

  return (
    <Track>
      <Box transform="translateX(-12px)">
        <Heading as="h1">
          <Menu
            label={
              <Button styleType="passive" alignIcon="right" display="block">
                <Text
                  as="span"
                  typography="megaStrong"
                  lineClamp={1}
                  color="textSolid"
                >
                  {watchlistName}
                </Text>
                <ChevronDownSmallSolid fill={theme.color.whiteRGBA(0.5)} />
              </Button>
            }
            items={items}
            placement="bottom start"
            onAction={handleAction}
            selectionMode="single"
            selectedKeys={watchlistId ? [watchlistId] : []}
          >
            {(item) => (
              <Section
                items={item.children}
                title={item.label}
                key={item.value}
                aria-label={item.label}
              >
                {(item) => (
                  <Item
                    textValue={item.label}
                    key={item.value}
                    aria-label={item.label}
                  >
                    {item.value === CREATE_NEW_WATCHLIST_KEY ? (
                      <CreateNewWatchlistOption {...item} />
                    ) : (
                      <Option
                        {...item}
                        $selected={watchlistId === item.value}
                      />
                    )}
                  </Item>
                )}
              </Section>
            )}
          </Menu>
        </Heading>
      </Box>

      {modal === 'create' && (
        <CreateWatchlistModal
          onClose={handleCloseModal}
          onCreate={handleCloseModal}
        />
      )}

      {modal === 'limited' && (
        <WatchlistsLimitModal
          onClose={handleCloseModal}
          watchlistsLimit={WATCHLISTS_LIMIT}
        />
      )}
    </Track>
  );
};
